import React, { useState, useEffect } from 'react';
import {
  Box, TextField, Grid, Typography, Checkbox, FormControlLabel, RadioGroup, Radio, MenuItem, Button, Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { Field, useFormikContext } from 'formik';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import dayjs from 'dayjs';
import { ncdAPI } from "../../../../services/ncd.api";

const HistoryForm = ({ setIsEditing: setParentIsEditing, individualId }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const { setFieldValue } = useFormikContext();

  const handleEditToggle = () => {
    const newEditingState = !isEditing;
    setIsEditing(newEditingState);
    if (setParentIsEditing) {
      setParentIsEditing(newEditingState);
    }
  };

  const fetchHistory = async () => {
    try {
      setIsPageLoading(true);
      if (!individualId) return;

      const response = await ncdAPI.getInitialAssessmentHistory(individualId);
      
      if (response?.data) {
        const data = response.data;
        
        // Convert API response to form values
        const convertToYesNo = (value) => {
          if (value === true) return 'yes';
          if (value === false) return 'no';
          return '';
        };

        // Update form fields with fetched data
        setFieldValue('history', {
          diabetes: data.diabts || false,
          hypertension: data.hypertsn || false,
          copd: data.copd || false,
          ckd: data.ckd || false,
          liverFailure: data.LiverDiseases || false,
          pvd: data.pvd || false,
          heartAttack: data.heartAtck || false,
          stroke: data.strk || false,
          heartDisease: data.heartDisease || false,
          diabetesVerified: data.diabetesReportVerified || false,
          hypertensionVerified: data.hypertensionReportVerified || false,
          copdVerified: data.copdReportVerified || false,
          ckdVerified: data.ckdReportVerified || false,
          liverFailureVerified: data.liverDiseasesReportVerified || false,
          pvdVerified: data.pvdReportVerified || false,
          heartAttackVerified: data.heartAttackReportVerified || false,
          strokeVerified: data.strkReportVerified || false,
          heartDiseaseVerified: data.heartDiseaseReportVerified || false,
          pregnant: convertToYesNo(data.pregnt),
          familyHistory: {
            prematureMI: convertToYesNo(data.fmlyHstryPreMI),
            stroke: convertToYesNo(data.fmlyHstryStrk),
            diabetes: convertToYesNo(data.fmlyHstryDiabts)
          },
          tobaccoConsumption: {
            smokesTobacco: convertToYesNo(data.smkng),
            smokelessTobacco: convertToYesNo(data.smkls)
          },
          alcoholConsumption: data.alcohol || ''
        });

        // Update history date if available
        if (data.assesDate) {
          // Convert DD-MM-YYYY to YYYY-MM-DD
          const [day, month, year] = data.assesDate.split('-');
          setFieldValue('historyDate', `${year}-${month}-${day}`);
        }
      }
    } catch (error) {
      console.error("Error fetching history data:", error);
    } finally {
      setIsPageLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
    // Expose fetchHistory to parent via window for refresh after submission
    window.historyFormRef = { fetchHistory };
    return () => {
      delete window.historyFormRef;
    };

  }, [individualId]);

  const conditions = [
    { key: "diabetes", label: "Diabetes" },
    { key: "hypertension", label: "Hypertension" },
    { key: "copd", label: "COPD" },
    { key: "ckd", label: "CKD" },
    { key: "liverFailure", label: "Liver Failure" },
    { key: "pvd", label: "PVD" },
    { key: "heartAttack", label: "Heart Attack" },
    { key: "stroke", label: "Stroke" },
    { key: "heartDisease", label: "Heart Disease" }
  ];

  return (
    <Box className="form-section">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" className="section-title">History</Typography>
        <Button
          variant="contained"
          color={isEditing ? "error" : "primary"}
          onClick={handleEditToggle}
          sx={{ textTransform: 'capitalize' }}
        >
          <EditOutlinedIcon /> {isEditing ? 'Cancel' : 'Edit'}
        </Button>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Field name="historyDate">
              {({ field, form, meta }) => (
                <DatePicker
                  label="Date"
                  format="DD/MM/YYYY"
                  value={field.value ? dayjs(field.value) : dayjs()}
                  onChange={(newValue) => {
                    form.setFieldValue('historyDate', newValue ? newValue.format('YYYY-MM-DD') : '');
                  }}
                  maxDate={dayjs()}
                  disabled={!isEditing}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: 'normal',
                      error: meta.touched && Boolean(meta.error),
                      helperText: meta.touched && meta.error,
                      sx: {
                        opacity: isEditing ? 1 : 0.7,
                        pointerEvents: isEditing ? 'auto' : 'none'
                      }
                    }
                  }}
                />
              )}
            </Field>
          </LocalizationProvider>
        </Grid>
      </Grid>


      <Box className="subsection">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    borderBottom: "none",
                    fontWeight: 600,
                    pl: 0,
                  }}
                >
                  Conditions
                </TableCell>
                <TableCell
                  sx={{
                    borderBottom: "none",
                    fontWeight: 600,
                  }}
                >
                  Medical reports viewed / verified ?
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {conditions.map((condition) => (
                <TableRow key={condition.key}>
                  <TableCell
                    sx={{
                      borderBottom: "none",
                      py: 0.5,
                      pl: 0,
                    }}
                  >
                    <Field name={`history.${condition.key}`}>
                      {({ field, form }) => (
                        <FormControlLabel
                          disabled={!isEditing}
                          control={
                            <Checkbox
                              checked={field.value}
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                // If unchecked, also uncheck the verified checkbox
                                if (!e.target.checked) {
                                  form.setFieldValue(`history.${condition.key}Verified`, false);
                                }
                              }}
                            />
                          }
                          label={condition.label}
                          sx={{ opacity: isEditing ? 1 : 0.7 }}
                        />
                      )}
                    </Field>
                  </TableCell>

                  <TableCell
                    sx={{
                      borderBottom: "none",
                      py: 0.5,
                    }}
                  >
                    <Field name={`history.${condition.key}Verified`}>
                      {({ field, form }) => {
                        const conditionChecked = form.values.history?.[condition.key];
                        return (
                          <FormControlLabel
                            disabled={!isEditing || !conditionChecked}
                            control={
                              <Checkbox
                                checked={field.value}
                                {...field}
                              />
                            }
                            label="Yes"
                            sx={{ opacity: (isEditing && conditionChecked) ? 1 : 0.7 }}
                          />
                        );
                      }}
                    </Field>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      <Box className="subsection" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Pregnant</Typography>
        <Field name="history.pregnant">
          {({ field, meta }) => (
            <>
              <RadioGroup {...field} row sx={{ opacity: isEditing ? 1 : 0.7 }}>
                <FormControlLabel
                  value="yes"
                  control={<Radio disabled={!isEditing} />}
                  label="Yes"
                  disabled={!isEditing}
                />
                <FormControlLabel
                  value="no"
                  control={<Radio disabled={!isEditing} />}
                  label="No"
                  disabled={!isEditing}
                />
              </RadioGroup>
              {meta.touched && meta.error && (
                <Typography variant="caption" color="error">{meta.error}</Typography>
              )}
            </>
          )}
        </Field>
      </Box>

      <Box className="subsection" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Family History of</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2"> Premature MI</Typography>
            <Field name="history.familyHistory.prematureMI">
              {({ field, meta }) => (
                <>
                  <RadioGroup {...field} row sx={{ opacity: isEditing ? 1 : 0.7 }}>
                    <FormControlLabel
                      value="yes"
                      control={<Radio disabled={!isEditing} />}
                      label="Yes"
                      disabled={!isEditing}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio disabled={!isEditing} />}
                      label="No"
                      disabled={!isEditing}
                    />
                  </RadioGroup>
                  {meta.touched && meta.error && (
                    <Typography variant="caption" color="error">{meta.error}</Typography>
                  )}
                </>
              )}
            </Field>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2">Stroke</Typography>
            <Field name="history.familyHistory.stroke">
              {({ field, meta }) => (
                <>
                  <RadioGroup {...field} row sx={{ opacity: isEditing ? 1 : 0.7 }}>
                    <FormControlLabel
                      value="yes"
                      control={<Radio disabled={!isEditing} />}
                      label="Yes"
                      disabled={!isEditing}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio disabled={!isEditing} />}
                      label="No"
                      disabled={!isEditing}
                    />
                  </RadioGroup>
                  {meta.touched && meta.error && (
                    <Typography variant="caption" color="error">{meta.error}</Typography>
                  )}
                </>
              )}
            </Field>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Typography variant="body2">Diabetes</Typography>
            <Field name="history.familyHistory.diabetes">
              {({ field, meta }) => (
                <>
                  <RadioGroup {...field} row sx={{ opacity: isEditing ? 1 : 0.7 }}>
                    <FormControlLabel
                      value="yes"
                      control={<Radio disabled={!isEditing} />}
                      label="Yes"
                      disabled={!isEditing}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio disabled={!isEditing} />}
                      label="No"
                      disabled={!isEditing}
                    />
                  </RadioGroup>
                  {meta.touched && meta.error && (
                    <Typography variant="caption" color="error">{meta.error}</Typography>
                  )}
                </>
              )}
            </Field>
          </Grid>
        </Grid>
      </Box>

      <Box className="subsection" sx={{ mt: 3 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Tobacco Consumption</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">Smokes tobacco</Typography>
            <Field name="history.tobaccoConsumption.smokesTobacco">
              {({ field, meta }) => (
                <>
                  <RadioGroup {...field} row sx={{ opacity: isEditing ? 1 : 0.7 }}>
                    <FormControlLabel
                      value="yes"
                      control={<Radio disabled={!isEditing} />}
                      label="Yes"
                      disabled={!isEditing}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio disabled={!isEditing} />}
                      label="No"
                      disabled={!isEditing}
                    />
                  </RadioGroup>
                  {meta.touched && meta.error && (
                    <Typography variant="caption" color="error">{meta.error}</Typography>
                  )}
                </>
              )}
            </Field>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2">Smokeless tobacco</Typography>
            <Field name="history.tobaccoConsumption.smokelessTobacco">
              {({ field, meta }) => (
                <>
                  <RadioGroup {...field} row sx={{ opacity: isEditing ? 1 : 0.7 }}>
                    <FormControlLabel
                      value="yes"
                      control={<Radio disabled={!isEditing} />}
                      label="Yes"
                      disabled={!isEditing}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio disabled={!isEditing} />}
                      label="No"
                      disabled={!isEditing}
                    />
                  </RadioGroup>
                  {meta.touched && meta.error && (
                    <Typography variant="caption" color="error">{meta.error}</Typography>
                  )}
                </>
              )}
            </Field>
          </Grid>
        </Grid>
      </Box>

      <Box className="subsection" sx={{ mt: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 'bold' }}>Alcohol Consumption</Typography>
            <Field name="history.alcoholConsumption">
              {({ field, meta }) => (
                <TextField
                  {...field}
                  select
                  fullWidth
                  placeholder="Select"
                  disabled={!isEditing}
                  error={meta.touched && Boolean(meta.error)}
                  helperText={meta.touched && meta.error}
                  sx={{
                    width: 250,
                    opacity: isEditing ? 1 : 0.7,
                    pointerEvents: isEditing ? 'auto' : 'none'
                  }}
                >
                  <MenuItem value="Daily or almost daily">Daily or almost daily</MenuItem>
                  <MenuItem value="Weekly">Weekly</MenuItem>
                  <MenuItem value="Monthly">Monthly</MenuItem>
                  <MenuItem value="Less than monthly">Less than monthly</MenuItem>
                  <MenuItem value="Never">Never</MenuItem>
                </TextField>
              )}
            </Field>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default HistoryForm;
